import { Edge, Node } from '@xyflow/react';

import { ParameterManager } from '../nodes/parameters/ParameterManager';
import { NodeConfig } from '../types/node-config';

export type NodeValidationError = {
  nodeId: string;
  parameterName: string;
  message: string;
};

export type NodeValidationResult = {
  isValid: boolean;
  errors: NodeValidationError[];
};

export type NodeValidationState = {
  [nodeId: string]: {
    isValid: boolean;
    errors: NodeValidationError[];
  };
};

/**
 * Service for validating workflow nodes and tracking their validation states
 */
export class NodeValidationService {
  private static instance: NodeValidationService;
  private parameterManager: ParameterManager;
  private validationState: NodeValidationState = {};
  private getNodeConfig: ((type: string) => NodeConfig | undefined) | null = null;

  private constructor() {
    this.parameterManager = new ParameterManager();
  }

  static getInstance(): NodeValidationService {
    if (!NodeValidationService.instance) {
      NodeValidationService.instance = new NodeValidationService();
    }
    return NodeValidationService.instance;
  }

  /**
   * Set the node config getter function (should be called once with useWorkflow's getNodeConfig)
   */
  setNodeConfigGetter(getter: (type: string) => NodeConfig | undefined): void {
    this.getNodeConfig = getter;
  }

  /**
   * Validate a single node and return validation result
   */
  validateNode(node: Node): NodeValidationResult {
    if (!this.getNodeConfig) {
      throw new Error(
        'NodeValidationService: getNodeConfig not set. Call setNodeConfigGetter first.'
      );
    }

    const nodeType = this.getNodeConfig(node.type || '');

    if (!nodeType) {
      return {
        isValid: false,
        errors: [
          {
            nodeId: node.id,
            parameterName: 'type',
            message: `Unknown node type: ${node.type}`,
          },
        ],
      };
    }

    const properties = nodeType.description.properties || [];
    const parameters = (node.data as Record<string, unknown>) || {};

    const validationResult = this.parameterManager.validateParameters(
      parameters,
      properties,
      node.type || ''
    );

    const errors: NodeValidationError[] = validationResult.errors.map(error => ({
      nodeId: node.id,
      parameterName: error.parameterName,
      message: error.message,
    }));

    const result = {
      isValid: validationResult.isValid,
      errors,
    };

    // Update validation state
    this.validationState[node.id] = result;

    return result;
  }

  /**
   * Validate multiple nodes and return combined results
   */
  validateNodes(nodes: Node[]): NodeValidationResult {
    const allErrors: NodeValidationError[] = [];
    let isValid = true;

    for (const node of nodes) {
      const result = this.validateNode(node);
      if (!result.isValid) {
        isValid = false;
        allErrors.push(...result.errors);
      }
    }

    return {
      isValid,
      errors: allErrors,
    };
  }

  /**
   * Get validation state for a specific node
   */
  getNodeValidationState(nodeId: string): { isValid: boolean; errors: NodeValidationError[] } {
    return this.validationState[nodeId] || { isValid: true, errors: [] };
  }

  /**
   * Check if a node has validation errors
   */
  hasValidationErrors(nodeId: string): boolean {
    const state = this.validationState[nodeId];
    return state ? !state.isValid : false;
  }

  /**
   * Clear validation state for a specific node
   */
  clearNodeValidation(nodeId: string): void {
    const { [nodeId]: _, ...newState } = this.validationState;
    this.validationState = newState;
  }

  /**
   * Clear validation state for multiple nodes
   */
  clearNodesValidation(nodeIds: string[]): void {
    let newState = this.validationState;
    nodeIds.forEach(nodeId => {
      const { [nodeId]: _, ...stateWithoutNode } = newState;
      newState = stateWithoutNode;
    });
    this.validationState = newState;
  }

  /**
   * Clear all validation state
   */
  clearAllValidation(): void {
    this.validationState = {};
  }

  /**
   * Get all current validation errors
   */
  getAllValidationErrors(): NodeValidationError[] {
    const allErrors: NodeValidationError[] = [];

    Object.values(this.validationState).forEach(state => {
      allErrors.push(...state.errors);
    });

    return allErrors;
  }

  /**
   * Get nodes with validation errors
   */
  getNodesWithErrors(): string[] {
    return Object.keys(this.validationState).filter(
      nodeId => !this.validationState[nodeId].isValid
    );
  }

  /**
   * Get the current validation state for all nodes
   */
  getValidationState(): NodeValidationState {
    return { ...this.validationState };
  }

  /**
   * Check if a node has any connections (incoming or outgoing)
   */
  hasAnyConnection(nodeId: string, edges: Edge[]): boolean {
    return edges.some(edge => edge.source === nodeId || edge.target === nodeId);
  }

  /**
   * Get nodes that have no connections
   */
  getIsolatedNodes(nodes: Node[], edges: Edge[]): Node[] {
    return nodes.filter(node => !this.hasAnyConnection(node.id, edges));
  }

  /**
   * Get child nodes within a ForLoop container
   */
  getForLoopChildNodes(forLoopNodeId: string, nodes: Node[]): Node[] {
    return nodes.filter(node => {
      const parentNode = node.parentNode || node.parentId;
      return parentNode === forLoopNodeId;
    });
  }

  /**
   * Validate node connectivity - only applies when there are multiple nodes
   */
  validateNodeConnectivity(nodes: Node[], edges: Edge[]): NodeValidationResult {
    const errors: NodeValidationError[] = [];

    // Group nodes by their parent (root level has no parent, ForLoop children have parent)
    const nodeGroups = new Map<string | null, Node[]>();

    nodes.forEach(node => {
      const parentId = node.parentNode || node.parentId || null;
      if (!nodeGroups.has(parentId)) {
        nodeGroups.set(parentId, []);
      }
      const group = nodeGroups.get(parentId);
      if (group) {
        group.push(node);
      }
    });

    // Validate each group separately
    nodeGroups.forEach((groupNodes, _parentId) => {
      // Only validate if there are multiple nodes in this group
      if (groupNodes.length > 1) {
        const isolatedNodes = this.getIsolatedNodes(groupNodes, edges);

        isolatedNodes.forEach(node => {
          errors.push({
            nodeId: node.id,
            parameterName: 'connectivity',
            message: 'Node is not connected to any other node',
          });
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate both node parameters and connectivity
   */
  validateNodesWithConnectivity(nodes: Node[], edges: Edge[]): NodeValidationResult {
    const parameterValidation = this.validateNodes(nodes);
    const connectivityValidation = this.validateNodeConnectivity(nodes, edges);

    return {
      isValid: parameterValidation.isValid && connectivityValidation.isValid,
      errors: [...parameterValidation.errors, ...connectivityValidation.errors],
    };
  }
}
